import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RotatingLines } from "react-loader-spinner";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { useSnackbar } from "notistack";

import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete,
  Edit,
  Search as SearchIcon
} from "@mui/icons-material";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import { collection, deleteDoc, doc, getDocs } from "firebase/firestore";

import CustomerDetails from "../components/CustomerDetails";
import CustomerForm from "../components/CustomerForm";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../firebase";

const formatPhoneNumber = phoneNumber => {
  if (!phoneNumber) return "";
  const phoneNumberParsed = parsePhoneNumberFromString(phoneNumber, "FR");
  if (phoneNumberParsed) {
    return phoneNumberParsed.formatInternational();
  }
  return phoneNumber;
};

const phoneGetURI = phoneNumber => {
  if (!phoneNumber) return "";
  const phoneNumberParsed = parsePhoneNumberFromString(phoneNumber, "FR");
  if (phoneNumberParsed) {
    return phoneNumberParsed.getURI();
  }
  return phoneNumber;
};

const getCustomerInitials = customer => {
  const first = (customer?.firstname || "").trim();
  const last = (customer?.lastname || "").trim();
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || "C";
};

const Customers = () => {
  const { currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const theme = useTheme();
  const { t } = useTranslation();

  // New state for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);

  useEffect(() => {
    const fetchCustomers = async () => {
      if (currentUser) {
        try {
          const customersRef = collection(db, `users/${currentUser.uid}/customers`);
          const snapshot = await getDocs(customersRef);
          const customersList = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          setCustomers(customersList);
          setLoading(false);
        } catch (error) {
          enqueueSnackbar(t("clients.notifications.fetchError") + error.message, {
            variant: "error"
          });
          setLoading(false);
        }
      }
    };

    fetchCustomers();
  }, [currentUser, enqueueSnackbar, t]);

  const handleOpenDialog = (customer, customerId = null) => {
    setSelectedCustomer(customer);
    setSelectedCustomerId(customerId);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCustomer(null);
    setSelectedCustomerId(null);
  };

  const handleOpenForm = (customer = null, customerId = null) => {
    setSelectedCustomer(customer);
    setSelectedCustomerId(customerId);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setSelectedCustomer(null);
    setSelectedCustomerId(null);
  };

  const handleSaveCustomer = newCustomer => {
    setCustomers(prevCustomers =>
      prevCustomers.some(customer => customer.id === newCustomer.id)
        ? prevCustomers.map(customer =>
          customer.id === newCustomer.id ? newCustomer : customer
        )
        : [...prevCustomers, newCustomer]
    );
    handleCloseForm();
  };

  // Modified to open confirmation dialog instead of deleting immediately
  const handleDeleteClick = customerId => {
    setCustomerToDelete(customerId);
    setDeleteDialogOpen(true);
  };

  // Actual delete function
  const handleDeleteCustomer = async () => {
    try {
      await deleteDoc(doc(db, `users/${currentUser.uid}/customers`, customerToDelete));
      setCustomers(customers.filter(customer => customer.id !== customerToDelete));
      enqueueSnackbar(t("clients.notifications.deleteSuccess"), {
        variant: "success"
      });
    } catch (error) {
      enqueueSnackbar(t("clients.notifications.deleteError") + error.message, {
        variant: "error"
      });
    } finally {
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
    }
  };

  const fullFirstName = customer => {
    const displayName = `${customer.firstname || ""} ${customer.lastname || ""}`.trim();

    if (customer.company) {
      return `${displayName || customer.firstname || ""} (${customer.company})`;
    }
    return displayName || customer.firstname;
  };

  const filteredCustomers = customers.filter(customer => {
    const fullName = `${customer.firstname || ""} ${customer.lastname || ""}`.toLowerCase();
    const email = (customer.email || "").toLowerCase();
    const search = searchTerm.toLowerCase().trim();

    if (!search) {
      return true;
    }

    return fullName.includes(search) || email.includes(search);
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <RotatingLines
          strokeColor={theme.palette.primary.main}
          strokeWidth="5"
          animationDuration="0.75"
          width="96"
          visible={true}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: "100%", mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          border: "1px solid #e2e8f0",
          background: theme =>
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(148, 163, 184, 0.14), rgba(71, 85, 105, 0.16))"
              : "#ffffff"
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                mb: 0.5,
                color: theme => theme.palette.mode === "dark" ? "primary.light" : "#0f172a",
                fontWeight: 800
              }}
            >
              {t("clients.pageTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredCustomers.length} / {customers.length} client(s)
            </Typography>
          </Box>
          {searchTerm && (
            <Chip
              label={`Filtre actif: ${searchTerm}`}
              variant="outlined"
              onDelete={() => setSearchTerm("")}
              deleteIcon={<CloseIcon />}
              sx={{
                backgroundColor: theme =>
                  theme.palette.mode === "dark"
                    ? "rgba(148, 163, 184, 0.12)"
                    : "rgba(15, 23, 42, 0.04)",
                borderColor: theme => theme.palette.divider
              }}
            />
          )}
        </Stack>
      </Paper>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", md: "center" }}
        sx={{ mb: 2 }}
      >
        <TextField
          variant="outlined"
          placeholder={t("common.search")}
          value={searchTerm}
          onChange={event => setSearchTerm(event.target.value)}
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2.5,
              backgroundColor: theme =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.04)"
                  : "#ffffff"
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            )
          }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenForm(null, null)}
          sx={{
            minWidth: { xs: "100%", md: 210 },
            height: 56,
            borderRadius: 2.5,
            fontWeight: 700,
            backgroundColor: theme =>
              theme.palette.mode === "dark" ? "rgba(148, 163, 184, 0.22)" : "#d4a411",
            color: theme => (theme.palette.mode === "dark" ? "#e2e8f0" : "#221d10"),
            boxShadow: "0 10px 24px rgba(212, 164, 17, 0.2)",
            "&:hover": {
              backgroundColor: theme =>
                theme.palette.mode === "dark" ? "rgba(148, 163, 184, 0.3)" : "#c2960f",
              boxShadow: "0 10px 24px rgba(212, 164, 17, 0.28)"
            }
          }}
        >
          {t("clients.addClient")}
        </Button>
      </Stack>

      <TableContainer 
        component={Paper} 
        sx={{ 
          borderRadius: 3,
          border: "1px solid #e2e8f0",
          boxShadow: theme => theme.palette.mode === "dark" 
            ? "0 4px 6px rgba(0, 0, 0, 0.4)"
            : "0 1px 2px rgba(15, 23, 42, 0.06)",
          overflow: "hidden",
          backgroundColor: theme => theme.palette.mode === "dark" 
            ? "background.paper"
            : "#fff"
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: theme => theme.palette.mode === "dark"
                  ? "rgba(148, 163, 184, 0.08)"
                  : "#f8fafc"
              }}
            >
              <TableCell sx={{ 
                fontWeight: 700,
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: theme => theme.palette.mode === "dark" ? "primary.light" : "#64748b",
                py: 2
              }}>
                {t("clients.name")}
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 700,
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: theme => theme.palette.mode === "dark" ? "primary.light" : "#64748b",
                py: 2
              }}>
                {t("clients.email")}
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 700,
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: theme => theme.palette.mode === "dark" ? "primary.light" : "#64748b",
                py: 2
              }}>
                {t("clients.phone")}
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 700,
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: theme => theme.palette.mode === "dark" ? "primary.light" : "#64748b",
                py: 2
              }}>
                {t("common.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCustomers.map(customer => (
              <TableRow 
                key={customer.id}
                sx={{
                  transition: "background-color 0.2s",
                  "&:hover": {
                    backgroundColor: theme => theme.palette.mode === "dark"
                      ? "rgba(148, 163, 184, 0.12)"
                      : "#f8fafc"
                  }
                }}
              >
                <TableCell sx={{ py: 1.75 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        fontSize: 14,
                        bgcolor: theme =>
                          theme.palette.mode === "dark"
                            ? "rgba(148, 163, 184, 0.2)"
                            : "rgba(15, 23, 42, 0.08)",
                        color: theme => (theme.palette.mode === "dark" ? "#cbd5e1" : "#334155")
                      }}
                    >
                      {getCustomerInitials(customer)}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                        {fullFirstName(customer)}
                      </Typography>
                      {customer.company && (
                        <Typography variant="caption" color="text.secondary">
                          {customer.company}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell sx={{ py: 1.75 }}>
                  <a
                    href={`mailto:${customer.email}`}
                    style={{
                      color: theme.palette.mode === "dark" ? theme.palette.primary.light : "#475569",
                      textDecoration: "none"
                    }}
                  >
                    {customer.email}
                  </a>
                </TableCell>
                <TableCell sx={{ py: 1.75 }}>
                  <a
                    href={phoneGetURI(customer?.phone)}
                    style={{
                      color: theme.palette.mode === "dark" ? theme.palette.primary.light : "#475569",
                      textDecoration: "none"
                    }}
                  >
                    {formatPhoneNumber(customer?.phone)}
                  </a>
                </TableCell>
                <TableCell sx={{ py: 1.75 }}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Tooltip title="Voir la fiche">
                      <IconButton
                        onClick={() => handleOpenDialog(customer, customer.id)}
                        size="small"
                        sx={{
                          color: theme => theme.palette.mode === "dark"
                            ? "primary.light"
                            : "#b98908",
                          "&:hover": {
                            backgroundColor: "rgba(212, 164, 17, 0.12)"
                          }
                        }}
                      >
                        <AccountBoxIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.edit")}>
                      <IconButton
                        onClick={() => handleOpenForm(customer, customer.id)}
                        size="small"
                        sx={{
                          color: theme => theme.palette.mode === "dark"
                            ? "primary.light"
                            : "#b98908",
                          "&:hover": {
                            backgroundColor: "rgba(212, 164, 17, 0.12)"
                          }
                        }}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.delete")}>
                      <IconButton
                        onClick={() => handleDeleteClick(customer.id)}
                        size="small"
                        sx={{ color: "error.main" }}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {filteredCustomers.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} sx={{ py: 6 }}>
                  <Box textAlign="center">
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      Aucun client trouvé
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Modifie ta recherche pour afficher d’autres résultats.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CustomerDetails
        open={dialogOpen}
        handleClose={handleCloseDialog}
        customer={selectedCustomer}
        customerId={selectedCustomerId}
      />

      <CustomerForm
        open={formOpen}
        handleClose={handleCloseForm}
        customer={selectedCustomer}
        customerId={selectedCustomerId}
        userId={currentUser.uid}
        onSave={handleSaveCustomer}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t("clients.deleteConfirmation.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("clients.deleteConfirmation.message")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            {t("common.cancel")}
          </Button>
          <Button onClick={handleDeleteCustomer} color="error">
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default Customers;
